import React, { useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  CreateTenantInput,
  TenantDto,
  useActivateTenant,
  useAdminTenants,
  useCreateTenant,
} from '@shared/api/hooks/useAdminTenants';

interface TenantFormValues {
  slug: string;
  name: string;
  primaryColor: string;
  secondaryColor: string;
  clubName: string;
  appName?: string;
  clubCity?: string;
  clubStadium?: string;
  clubFounded?: number;
}

export function TenantsPage(): React.ReactElement {
  const [createModal, setCreateModal] = useState(false);
  const [form] = Form.useForm<TenantFormValues>();

  const { data, isLoading, refetch, isFetching } = useAdminTenants();
  const tenants = data ?? [];

  const createMutation = useCreateTenant();
  const activateMutation = useActivateTenant();

  const onSubmitCreate = async (): Promise<void> => {
    const v = await form.validateFields();
    try {
      const input: CreateTenantInput = {
        slug: v.slug.toLowerCase().trim(),
        name: v.name,
        primaryColor: v.primaryColor,
        secondaryColor: v.secondaryColor,
        clubName: v.clubName,
        appName: v.appName || undefined,
        clubCity: v.clubCity || undefined,
        clubStadium: v.clubStadium || undefined,
        clubFounded: v.clubFounded || undefined,
      };
      await createMutation.mutateAsync(input);
      message.success('Tenant creato');
      setCreateModal(false);
      form.resetFields();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : msg ?? 'Errore');
    }
  };

  const onToggleActive = async (id: string, active: boolean): Promise<void> => {
    try {
      await activateMutation.mutateAsync({ id, active });
      message.success(active ? 'Tenant attivato' : 'Tenant disattivato');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Errore');
    }
  };

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Tenant (SUPER_ADMIN)
          </Typography.Title>
          <Tag>{tenants.length}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Aggiorna
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModal(true)}>
            Nuovo tenant
          </Button>
        </Space>
      }
    >
      <Table<TenantDto>
        rowKey="id"
        loading={isLoading}
        dataSource={tenants}
        pagination={false}
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: 'Slug',
            dataIndex: 'slug',
            width: 130,
            render: (v: string) => <Tag color="purple">{v}</Tag>,
          },
          { title: 'Nome', dataIndex: 'name' },
          {
            title: 'Club',
            render: (_v, t) => t.config?.clubName ?? '—',
          },
          {
            title: 'Branding',
            width: 130,
            render: (_v, t) => {
              if (!t.config) return '—';
              return (
                <Space size={4}>
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 4,
                      background: t.config.primaryColor,
                      border: '1px solid #d9d9d9',
                    }}
                    title={t.config.primaryColor}
                  />
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 4,
                      background: t.config.secondaryColor,
                      border: '1px solid #d9d9d9',
                    }}
                    title={t.config.secondaryColor}
                  />
                </Space>
              );
            },
          },
          {
            title: 'Stato',
            dataIndex: 'isActive',
            width: 130,
            render: (v: boolean, r) => (
              <Switch
                checked={v}
                onChange={(checked) => onToggleActive(r.id, checked)}
                checkedChildren="Attivo"
                unCheckedChildren="Off"
                loading={activateMutation.isPending}
              />
            ),
          },
          {
            title: 'Creato',
            dataIndex: 'createdAt',
            width: 130,
            render: (v: string) => new Date(v).toLocaleDateString('it-IT'),
          },
        ]}
      />

      <Modal
        title="Nuovo tenant"
        open={createModal}
        onCancel={() => setCreateModal(false)}
        onOk={onSubmitCreate}
        okText="Crea"
        confirmLoading={createMutation.isPending}
        destroyOnHidden
        width={620}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="slug"
            label="Slug"
            extra="Identificativo URL — lowercase, no spazi (es. lanciano, latina)"
            rules={[
              { required: true, min: 2, max: 50 },
              { pattern: /^[a-z0-9-]+$/, message: 'Solo lowercase, numeri, trattini' },
            ]}
          >
            <Input placeholder="lanciano" />
          </Form.Item>
          <Form.Item name="name" label="Nome interno" rules={[{ required: true, max: 255 }]}>
            <Input placeholder="Lanciano FC" />
          </Form.Item>
          <Form.Item name="clubName" label="Nome club (visualizzato)" rules={[{ required: true, max: 255 }]}>
            <Input placeholder="Lanciano FC" />
          </Form.Item>
          <Form.Item name="appName" label="Nome app">
            <Input placeholder="Lanciano FC" />
          </Form.Item>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Form.Item
              name="primaryColor"
              label="Colore primario"
              rules={[{ required: true }]}
              style={{ width: 280 }}
            >
              <Input type="color" />
            </Form.Item>
            <Form.Item
              name="secondaryColor"
              label="Colore secondario"
              rules={[{ required: true }]}
              style={{ width: 280 }}
            >
              <Input type="color" />
            </Form.Item>
          </Space>
          <Form.Item name="clubCity" label="Città">
            <Input placeholder="Lanciano" />
          </Form.Item>
          <Form.Item name="clubStadium" label="Stadio">
            <Input placeholder="Stadio Guido Biondi" />
          </Form.Item>
          <Form.Item name="clubFounded" label="Anno fondazione">
            <InputNumber min={1850} max={2100} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
